import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { BusinessComponent } from '@blk/simba';
import { ViewCategory } from '@ddt-web/config';
import { ConfigService, InjectConfig, ModelsExperienceConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { ComponentData } from '@ddt-web/data-service';
import { DialogService } from '@ddt-web/dialog';
import { AchoReportConfiguration } from '@ddt-web/pdf-utils';
import { StoreService } from '@ddt-web/store-service';
import { PartnerUserProfileService } from '@ddt-web/user-data';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import { first } from 'rxjs/operators';

import { PdfGenerateStoreConfig } from '../../config/store.config';
import { PdfReportConfig, PdfReportSection } from '../../models/pdf-report-config.interface';
import { PdfReportConfigService } from '../../services/pdf-report-config.service';
import { PdfCustomPageComponent } from '../pdf-custom-page/pdf-custom-page.component';

@UntilDestroy()
@Component({
	selector: 'ac-pdf-report-customization',
	templateUrl: './pdf-report-customization.component.html',
	styleUrls: ['./pdf-report-customization.component.scss']
})
export class PdfReportCustomizationComponent implements OnInit, OnChanges {

	@InjectConfig(ModelsExperienceConfigKey.SHOW_CMS_PDF_MODULARITY) private readonly showCmsPdfModularity: boolean;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_PDF_MODULARITY_FOOTNOTE) public readonly showCustomizationFootNote: boolean;


	@Input() private isBlkModel: boolean;
	@Input() public reportType: string;
	@Input() public mode: string = ''; // PdfCustomisationMode, default to '' as it is used in tracking & DCR keys
	@Input() public showPurposeValidation?: boolean; // for proposal implementation
	@Input() public achoReportConfiguration: AchoReportConfiguration;
	@Input() public componentDataMapping: Map<string, ComponentData>;
	@Input() public showOptionalCol: boolean = true;
	@Input() public showCustomization: boolean = false;

	@Output() public onLoadingComplete: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() public onConfigChange: EventEmitter<PdfReportConfig> = new EventEmitter<PdfReportConfig>();
	@Output() public onConfigToggle: EventEmitter<boolean> = new EventEmitter<boolean>();

	public dataDrivenSections: string[] = [];
	public isCustomizationAvailable: boolean = false;
	public isCustomizationLoading: boolean = true;

	public showOrdering: boolean;
	public showAddCustomPage: boolean;

	public isToggleAll: boolean = false;

	public currentStoreConfigName: string;
	public currentConfig: PdfReportConfig = { sections: [] };
	public customPageDialogRef;
	public isPartnerUser: boolean;
	public showInfobubble: boolean;
	public ViewCategory = ViewCategory;

	constructor(
		private reportConfigService: PdfReportConfigService,
		private contentService: ContentService,
		private dialogService: DialogService,
		private storeService: StoreService,
		private partnerUserProfileService: PartnerUserProfileService,
		private configService: ConfigService
	) { }


	public ngOnInit(): void {
		this.showOrdering = this.configService.getBoolean(SharedConfigKey.SHOW_PDF_REORDERING, false);
		this.showAddCustomPage = this.configService.getBoolean(SharedConfigKey.SHOW_ADD_CUSTOM_PAGE, false); // enables the add custom page button. False by default.
		this.showInfobubble = this.contentService.getBoolean('SHOW_PDF_CUSTOMIZATION_INFOBUBBLE', false);
		this.partnerUserProfileService
			.isPartnerUser()
			.pipe(first())
			.subscribe(isPartnerUser => (this.isPartnerUser = isPartnerUser));
	}

	public ngOnChanges(changes: SimpleChanges): void {
		const changesFields = Object.keys(changes);
		const onValidationChangeOnly = changesFields.length === 1 && changesFields.includes('showPurposeValidation');
		if (onValidationChangeOnly) {
			this.showCustomization = true;
		} else if (this.reportType && this.achoReportConfiguration && this.componentDataMapping && !get(changes, 'achoReportConfiguration.previousValue')) {
			this.currentStoreConfigName = this.getStoreName(this.reportType, this.mode);
			this.reportConfigService.getConfigFromStore(this.currentStoreConfigName).pipe(first(), untilDestroyed(this)).subscribe(data => {
				if (data) {
					this.updatePdfConfig(data, changes);
				} else {
					this.reportConfigService.fetchPdfReportConfig(this.reportType, this.mode).pipe(first(), untilDestroyed(this)).subscribe(config => {
						this.reportConfigService.addConfigToStore(this.currentStoreConfigName, config);
						this.updatePdfConfig(config, changes);
					});
				}
			});
		}
	}

	private updatePdfConfig(config: PdfReportConfig, changes: SimpleChanges): void {
		if (this.showCmsPdfModularity && !this.isBlkModel) {
			this.updateCustomizationMode(config.cmsSections);
			this.currentConfig = cloneDeep(config.cmsSections);

		} else {
			this.updateCustomizationMode(config);
			this.currentConfig = cloneDeep(config);
		}
		this.updateVisibilityRules();
		this.currentConfig = this.moveNonVisiblePagesToEnd(this.currentConfig);
		if (this.isPartnerUser) {
			this.updatePartnerConfig();
		}
		this.dataDrivenSections = this.currentConfig.sections.filter(section => section.subsections?.every(subsection => subsection.dataMapping)).map(section => section.nameKey);
		this.setToggleAllStatus();
	}

	public toggleShowCustomization(): void {
		this.showCustomization = !this.showCustomization;
		this.onConfigToggle.emit(this.showCustomization);
	}

	public toggleAll(event: MouseEvent): void {
		// When click on All Sections checkbox
		const checkbox: HTMLInputElement = <HTMLInputElement>event.currentTarget;
		this.isToggleAll = checkbox.checked;
		const configCopy = cloneDeep(this.currentConfig);
		configCopy.sections.forEach(section => {
			if (section.optional) {
				section.checked = this.isToggleAll; // set each optional section checkbox to isToggleAll
			}
			if (section.subsections) {
				section.subsections.forEach(subsection => {
					if (!subsection.isNotOptional) {
						subsection.checked = this.isToggleAll; // set each subsection checkbox to isToggleAll
					}
				});
			}
		});
		this.currentConfig = configCopy;
		this.emitConfig();
	}

	// need to update this for proposal view only for driving 'how i got you there'
	public onSectionCheck(sectionIndex: number, event: MouseEvent): void {
		// When click on the checkbox of each section
		const checkbox: HTMLInputElement = <HTMLInputElement>event.currentTarget;
		const checked = checkbox.checked;
		const configCopy = cloneDeep(this.currentConfig);
		const sectionCopy = configCopy.sections[sectionIndex];
		sectionCopy.checked = checked;
		if (sectionCopy.subsections) {
			sectionCopy.subsections.forEach(subsection => {
				subsection.checked = checked; // set each subsection accordingly
			});
		}
		this.currentConfig = configCopy;
		if (this.currentConfig.sections.filter(section => section.optional && section.visible).every(section => section.checked === checked)) {
			this.toggleAll(event);
		}
		else {
			this.isToggleAll = false;
		}
		this.emitConfig();
	}

	public onSubsectionCheck(sectionIndex: number, subsectionIndex: number, event: MouseEvent): void {
		// Section.checked == true iff some subsection.checked == true
		const checkbox: HTMLInputElement = <HTMLInputElement>event.currentTarget;
		const checked = checkbox.checked;
		const configCopy = cloneDeep(this.currentConfig);
		const sectionCopy = configCopy.sections[sectionIndex];
		const subsectionCopy = sectionCopy.subsections[subsectionIndex];
		subsectionCopy.checked = checked;
		sectionCopy.checked = sectionCopy.subsections.some(subsection => subsection.visible && subsection.checked);
		this.currentConfig = configCopy;
		this.setToggleAllStatus();
		this.emitConfig();
	}

	public moveSectionUp(section: PdfReportSection): void {
		const configClone = cloneDeep(this.currentConfig);
		const currentSection = configClone.sections.find(currentSection => currentSection.order === section.order);
		const prevSection = configClone.sections.find(prevSection => prevSection.order === section.order - 1);
		if (this.sectionIsOrderable(prevSection)) {
			prevSection.order++;
			currentSection.order--;
			this.currentConfig = configClone;
			this.orderSections();
			this.emitConfig();
		}
	}

	public moveSectionDown(section: PdfReportSection): void {
		const configClone = cloneDeep(this.currentConfig);
		const currentSection = configClone.sections.find(currentSection => currentSection.order === section.order);
		const nextSection = configClone.sections.find(nextSection => nextSection.order === section.order + 1);
		if (this.sectionIsOrderable(nextSection)) {
			nextSection.order--;
			currentSection.order++;
			this.currentConfig = configClone;
			this.orderSections();
			this.emitConfig();
		}
	}

	public previousSectionIsOrderable(section: PdfReportSection): boolean {
		const prevSection = this.currentConfig.sections.find(prevSection => prevSection.order === section.order - 1);
		return this.sectionIsOrderable(prevSection);
	}

	public nextSectionIsOrderable(section: PdfReportSection): boolean {
		const nextSection = this.currentConfig.sections.find(nextSection => nextSection.order === section.order + 1);
		return this.sectionIsOrderable(nextSection);
	}

	private updateCustomizationMode(pdfReportConfig: PdfReportConfig): void {
		this.isCustomizationLoading = false;
		this.onLoadingComplete.emit(true);
		this.isCustomizationAvailable = !isEmpty(get(pdfReportConfig, 'sections', []));
	}

	private sectionIsOrderable(section: PdfReportSection): boolean {
		return section && section.visible && section.isOrderable;
	}

	private orderSections(): void {
		this.currentConfig.sections.sort((s1, s2) => s1.order - s2.order);
	}

	private updateVisibilityRules(): void {
		const componentsWithNoDataOrNotVisible: string[] = [];

		// check how the support for this should work since we do not have a separate report for this mode
		this.achoReportConfiguration.pages.forEach(section => {
			section.components.forEach(component => {
				const businessComp = component as BusinessComponent;

				if (businessComp?.businessComponentOptions?.visible === false) {
					componentsWithNoDataOrNotVisible.push(businessComp.name);
					return;
				}

				const componentId = get(businessComp, 'businessComponentOptions.componentId');
				if (componentId) {
					const isDataAvailable = businessComp.isComponentDataVisible(this.componentDataMapping.get(componentId));
					if (!isDataAvailable) {
						componentsWithNoDataOrNotVisible.push(businessComp.name);
					}
				}
			});
		});

		if (componentsWithNoDataOrNotVisible.length) {
			this.currentConfig.sections.forEach(section => {
				if (section.componentNames && section.componentNames.every(component => componentsWithNoDataOrNotVisible.includes(component))) {
					section.visible = false;
					section.checked = false;
				} else if (section.subsections) {
					section.subsections.forEach(subsection => {
						if (subsection.componentNames.every(component => componentsWithNoDataOrNotVisible.includes(component))) {
							subsection.visible = false;
							subsection.checked = false;
						}
					});
					if (section.subsections.every(subsection => subsection.visible === false)) {
						section.visible = false;
						section.checked = false;
					}
				}
			});
		}

		this.currentConfig.sections.forEach(section => {
			const dcrKey = 'SHOW_PDF_' + section.nameKey.replace('_KEY', ''); // OVERVIEW_SECTION_KEY -> SHOW_PDF_OVERVIEW_SECTION
			const dcrOverrideVisible = this.contentService.getBoolean(dcrKey, true);
			if (!dcrOverrideVisible) {
				section.visible = false;
				section.checked = false;
			}
		});

		this.emitConfig();
	}

	public openCustomPageDialog(): void {
		this.customPageDialogRef = this.dialogService.open(PdfCustomPageComponent, {
			showClose: true
		});
		this.customPageDialogRef.afterClosed.pipe(first()).subscribe((customPages: PdfReportConfig) => {
			if (customPages?.sections?.length) {
				const currentConfigClone = cloneDeep(this.currentConfig);
				const reversedReportSectionsCopy = [...this.currentConfig.sections].reverse();
				const finalOrderablePageIndex = this.currentConfig.sections.length - reversedReportSectionsCopy.findIndex(section => section.isOrderable && section.visible);

				customPages.sections.forEach((section: PdfReportSection, index: number) => {
					section.order = finalOrderablePageIndex + index;
				});
				currentConfigClone.sections.splice(finalOrderablePageIndex, 0, ...customPages.sections);

				this.currentConfig = currentConfigClone;
				this.emitConfig();
			}
		});
	}

	public closeCustomPageDialog(): void {
		this.dialogService.close(this.customPageDialogRef);
	}

	public emitConfig(): void {
		this.onConfigChange.emit(this.currentConfig);
	}

	public deleteCustomPage(customPage: PdfReportSection): void {
		const configCopy = cloneDeep(this.currentConfig);
		configCopy.sections = configCopy.sections.filter(section => section.nameKey !== customPage.nameKey);
		this.removeImageFromStore(customPage);

		for (let i = customPage.order; i < configCopy.sections.length; i++) {
			if (configCopy.sections[i].order) {
				configCopy.sections[i].order--;
			}
		}

		this.currentConfig = configCopy;
		this.emitConfig();
	}

	private removeImageFromStore(customPage: PdfReportSection): void {
		let reportConfigClone;
		this.storeService.selectAll(PdfGenerateStoreConfig.reportConfig).subscribe(state => {
			reportConfigClone = cloneDeep(state);
			const { [customPage.nameKey]: removedPage, ...pagesToKeep } = reportConfigClone.customPages;
			reportConfigClone.customPages = pagesToKeep;
		});
		this.storeService.upsertOne(PdfGenerateStoreConfig.reportConfig, reportConfigClone);
	}

	private updatePartnerConfig(): void {
		// For partner, we want the performance section to be mandatory
		// If more requirements come, we should create a separate modularity file for partner
		const configCopy = cloneDeep(this.currentConfig);
		const performanceSection = configCopy.sections.find(section => section.nameKey === 'PERFORMANCE_SECTION_KEY');
		if (performanceSection) {
			performanceSection.optional = false;
		}
		this.currentConfig = configCopy;
		this.emitConfig();
	}

	private getStoreName(reportType: string, mode?: string): string {
		if (mode === 'SNAPSHOT') {
			return 'snapshotReportConfig';
		}
		switch (reportType) {
			case ViewCategory.FA_PLUS_PDF:
				return 'psReportConfig';
			case ViewCategory.ADVISOR_PDF:
				return 'advisorReportConfig';
			case ViewCategory.CLIENT_PDF:
				return 'clientReportConfig';
			case ViewCategory.FA_SELF_SERVICE_PDF:
				return 'advisorReportConfig';
			case ViewCategory.PROPOSAL_PDF:
				return 'proposalReportConfig';
		}
	}

	// A semi-temporary fix for a reordering bug that prevented pages moving around invisible components (e.g. when they have no data)
	// This was noticed with custom pages which are always added to the end of a config
	// TODO: Talk to Jen to explore the possibility of removing invisible pages from the config
	private moveNonVisiblePagesToEnd(pdfReportConfig: PdfReportConfig): PdfReportConfig {
		const visibleSections = pdfReportConfig.sections.filter(section => section.visible);
		const invisibleSections = pdfReportConfig.sections.filter(section => !section.visible);

		return { sections: [...visibleSections, ...invisibleSections].map((section, index) => { section.order = index; return section; }) };
	}

	private setToggleAllStatus(): void {
		this.isToggleAll = this.currentConfig.sections.filter(section => section.optional && section.visible).every(section => section.checked);
	}
}

